'use client';

import { useEffect, useRef, useState } from 'react';
import { Loader } from '@/components/ui/loader';
import { useLocale } from 'next-intl';
import { getStaticUiCopy } from '@/i18n/static-ui-copy';
import {
  allowModelViewerSource,
  installModelViewerUrlPolicy,
} from '@/lib/model-viewer-url-policy';

interface ModelViewerProps {
  src: string;
  alt?: string;
  className?: string;
  autoRotate?: boolean;
  cameraControls?: boolean;
  ar?: boolean;
  style?: React.CSSProperties;
  onReady?: () => void;
  /** Called once with the shown message when the model fails or times out. */
  onError?: (message: string) => void;
}

export function ModelViewer({
  src,
  alt = '3D Model',
  className = '',
  autoRotate = false,
  cameraControls = true,
  ar = true,
  style,
  onReady,
  onError,
}: ModelViewerProps) {
  const locale = useLocale();
  const ui = getStaticUiCopy(locale);
  const modelViewerRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  // The model itself (not just the library) has drawn. Until then a loader
  // covers the viewer: <model-viewer> renders nothing while a GLB downloads,
  // which read as a blank page for large models (#4920).
  const [modelReady, setModelReady] = useState(false);
  // Refs, so an unstable callback never tears down and reloads the model.
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onReadyRef.current = onReady;
    onErrorRef.current = onError;
  }, [onReady, onError]);

  // Prevent hydration mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    // Only load in browser environment and after component is mounted
    if (typeof window === 'undefined' || !isMounted) return;

    // Load <model-viewer> only in the browser with proper async handling
    console.log(
      'ModelViewer: Attempting to load @google/model-viewer library...'
    );

    const loadModelViewer = async () => {
      try {
        // Use dynamic import with proper async handling for Next.js 15.5.2
        // this is the only place where we can use dynamic imports are ok
        // never ever use dynamic imports in convex
        await import('@google/model-viewer');

        // Wait for custom element to be defined
        if ('customElements' in window) {
          await customElements.whenDefined('model-viewer');
        }
        // Every model load may only reach the model file itself (and data:/
        // blob:), never a host named inside the glTF. Fail closed without it.
        if (!installModelViewerUrlPolicy(customElements.get('model-viewer'))) {
          setError(ui.failedToLoad3dViewerLibrary);
          return;
        }

        console.log('ModelViewer: @google/model-viewer loaded successfully');
        setIsLoaded(true);
      } catch (error) {
        console.error(
          'ModelViewer: Failed to load @google/model-viewer:',
          error
        );
        setError(ui.failedToLoad3dViewerLibrary);
      }
    };

    void loadModelViewer();
  }, [isMounted]);

  useEffect(() => {
    if (isLoaded && modelViewerRef.current && src) {
      console.log('ModelViewer: Setting up model viewer with src:', src);

      // Clear any existing content
      modelViewerRef.current.innerHTML = '';
      setModelReady(false);
      const fail = (message: string) => {
        setError(message);
        onErrorRef.current?.(message);
      };
      // This model file (and nothing it names) may load while it is shown.
      const releaseSource = allowModelViewerSource(src);

      // Create the model-viewer element
      const modelViewer = document.createElement('model-viewer');

      // Set all attributes
      modelViewer.setAttribute('src', src);
      modelViewer.setAttribute('alt', alt);
      // `ar` is a boolean attribute: its presence enables AR, whatever its
      // value. Phones without WebXR fall back to Scene Viewer (Android) or
      // Quick Look (iOS, with a USDZ generated from the GLB on the fly).
      modelViewer.toggleAttribute('ar', ar);
      if (ar) {
        modelViewer.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
      }
      modelViewer.setAttribute('auto-rotate', autoRotate.toString());
      modelViewer.setAttribute('camera-controls', cameraControls.toString());
      modelViewer.setAttribute('loading', 'eager');
      modelViewer.setAttribute('reveal', 'auto');
      modelViewer.setAttribute('environment-image', 'neutral'); // Add neutral lighting
      modelViewer.setAttribute('shadow-intensity', '1'); // Add shadow
      // Add interaction-policy for better control
      modelViewer.setAttribute('interaction-policy', 'allow-when-focused');
      modelViewer.style.width = '100%';
      modelViewer.style.height = '100%';
      modelViewer.style.minHeight = '200px';
      modelViewer.style.display = 'block';
      modelViewer.style.backgroundColor = 'transparent'; // Transparent background

      // Add more debugging
      console.log('ModelViewer: Created element with attributes:', {
        src: modelViewer.getAttribute('src'),
        ar: modelViewer.getAttribute('ar'),
        autoRotate: modelViewer.getAttribute('auto-rotate'),
        cameraControls: modelViewer.getAttribute('camera-controls'),
      });

      // Track loading progress
      let progressReported = false;
      // Once this model has drawn, a late error event (a texture or other
      // resource) must not swap the working viewer for the error panel.
      let modelDrawn = false;
      let fileSizeBytes = 0;
      const headerController = new AbortController();

      // Check file size via HEAD request (for timeout calculation)
      fetch(src, { method: 'HEAD', signal: headerController.signal })
        .then((response) => {
          console.log('ModelViewer: File headers:', {
            contentType: response.headers.get('content-type'),
            contentLength: response.headers.get('content-length'),
            status: response.status,
            url: response.url,
          });

          const contentLength = response.headers.get('content-length');
          if (contentLength) {
            fileSizeBytes = parseInt(contentLength, 10);
            console.log(
              `ModelViewer: File size: ${Math.round((fileSizeBytes / 1024 / 1024) * 100) / 100}MB`
            );
          }

          const contentType = response.headers.get('content-type');
          if (contentType === 'application/octet-stream') {
            console.warn(
              'ModelViewer: File served with generic MIME type. This might cause loading issues.'
            );
          }
        })
        .catch((error) => {
          if (error instanceof DOMException && error.name === 'AbortError') {
            return;
          }
          console.warn('ModelViewer: Could not fetch file headers:', error);
        });

      // Use a longer timeout for large 3D models (90 seconds default, which should handle 20MB+ files)
      // The HEAD request is async, so we use a conservative default timeout
      const timeoutMs = 90000; // 90 seconds - sufficient for large GLB files

      // Add a timeout to detect if model never loads
      const loadTimeout = setTimeout(() => {
        if (!progressReported) {
          console.warn(
            `ModelViewer: Model loading timeout after ${timeoutMs / 1000} seconds`
          );
          console.warn('ModelViewer: Current element attributes:', {
            src: modelViewer.getAttribute('src'),
          });
          console.warn(
            'ModelViewer: No progress events received - file may be too large or URL inaccessible'
          );
          fail(ui.failedToLoad3dModel);
        } else {
          console.warn(
            `ModelViewer: Model loading timeout after ${timeoutMs / 1000} seconds, but progress was reported`
          );
          fail(ui.failedToLoad3dModel);
        }
      }, timeoutMs);

      // `load` fires before the model is on screen: model-viewer then moves
      // its WebGL canvas into the element on the first visible frame, which
      // presents blank for a frame or two. Swap the loader for the model only
      // once the element reports the model visible and has drawn a few frames
      // since, so the fade runs over pixels (#4925 TASTE).
      // Once per model: a repeated load restarts the wait, never a second
      // reveal, and nothing fires after cleanup. Each wait carries its own
      // generation, so a frame or timer already dequeued from an earlier
      // wait returns instead of counting toward (or firing) the new one.
      let revealed = false;
      let disposed = false;
      let generation = 0;
      let revealFrame = 0;
      let revealTimer: ReturnType<typeof setTimeout> | undefined;
      const reveal = () => {
        if (revealed || disposed) return;
        revealed = true;
        cancelAnimationFrame(revealFrame);
        clearTimeout(revealTimer);
        setModelReady(true);
        onReadyRef.current?.();
      };
      const revealWhenDrawn = () => {
        if (revealed || disposed) return;
        const wait = ++generation;
        cancelAnimationFrame(revealFrame);
        clearTimeout(revealTimer);
        let visibleFrames = 0;
        const tick = () => {
          if (revealed || disposed || wait !== generation) return;
          const visible =
            (modelViewer as unknown as { modelIsVisible?: boolean })
              .modelIsVisible === true;
          if (visible) visibleFrames += 1;
          if (visibleFrames >= 3) {
            reveal();
            return;
          }
          revealFrame = requestAnimationFrame(tick);
        };
        revealFrame = requestAnimationFrame(tick);
        // Frames may never run (a hidden tab, an off-screen or zero-size
        // element), and off screen the model never reports visible.
        revealTimer = setTimeout(() => {
          if (wait === generation) reveal();
        }, 1000);
      };

      // Add comprehensive event listeners
      const handleLoad = () => {
        console.log('ModelViewer: Model loaded successfully');
        clearTimeout(loadTimeout);
        progressReported = true;
        modelDrawn = true;
        setError(null);
        revealWhenDrawn();
      };

      const handleError = (event: Event) => {
        console.warn('ModelViewer: Error loading model:', event);
        if (modelDrawn) return;
        clearTimeout(loadTimeout);
        fail(ui.failedToLoad3dModel);
      };

      const handleModelVisibility = (event: Event) => {
        console.log('ModelViewer: Model visibility changed:', event);
      };

      const handleProgress = (event: Event) => {
        progressReported = true;
        // The progress event from model-viewer can be either ProgressEvent or CustomEvent
        const progressEvent = event as unknown as CustomEvent<{
          totalProgress: number;
        }>;
        const progress = progressEvent.detail?.totalProgress ?? 0;
        console.log(
          'ModelViewer: Loading progress:',
          `${Math.round(progress * 100)}%`
        );
      };

      const handleBeforeRender = () => {
        console.log('ModelViewer: Before render event');
      };

      const handleCameraChange = () => {
        console.log('ModelViewer: Camera change event');
      };

      modelViewer.addEventListener('load', handleLoad);
      modelViewer.addEventListener('error', handleError);
      modelViewer.addEventListener('model-visibility', handleModelVisibility);
      modelViewer.addEventListener('progress', handleProgress);
      modelViewer.addEventListener('before-render', handleBeforeRender);
      modelViewer.addEventListener('camera-change', handleCameraChange);

      // Log when element is added to DOM
      console.log('ModelViewer: Appending model-viewer element to DOM');
      modelViewerRef.current.appendChild(modelViewer);

      // Check if element was properly added
      const verifyTimer = setTimeout(() => {
        const el = modelViewerRef.current?.querySelector('model-viewer');
        const elementSrc =
          el instanceof Element ? el.getAttribute('src') : null;
        console.log('ModelViewer: Element in DOM check:', {
          elementExists: !!el,
          elementSrc,
        });
      }, 1000);

      return () => {
        headerController.abort();
        clearTimeout(loadTimeout);
        clearTimeout(verifyTimer);
        disposed = true;
        generation += 1;
        cancelAnimationFrame(revealFrame);
        clearTimeout(revealTimer);
        modelViewer.removeEventListener('load', handleLoad);
        modelViewer.removeEventListener('error', handleError);
        modelViewer.removeEventListener(
          'model-visibility',
          handleModelVisibility
        );
        modelViewer.removeEventListener('progress', handleProgress);
        modelViewer.removeEventListener('before-render', handleBeforeRender);
        modelViewer.removeEventListener('camera-change', handleCameraChange);
        modelViewer.remove();
        releaseSource();
      };
    }

    return undefined;
  }, [isLoaded, src, alt, ar, autoRotate, cameraControls]);

  if (error) {
    return (
      <div
        role="alert"
        className={`${className} bg-muted/40 text-muted-foreground flex items-center justify-center rounded-md p-4 text-center text-sm`}
        style={style}
      >
        {error}
      </div>
    );
  }

  if (isLoaded && !src) {
    return (
      <div
        className={`${className} flex items-center justify-center bg-red-100`}
        style={style}
      >
        <div className="text-sm text-red-500">{ui.noSrcProvided}</div>
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{
        minHeight: '200px', // Ensure minimum height
        minWidth: '200px', // Ensure minimum width
        display: 'block', // Ensure it's displayed
        position: 'relative', // Ensure proper positioning
        backgroundColor: 'transparent', // Transparent background
        overflow: 'hidden', // Prevent overflow
        ...style,
      }}
    >
      {/* One box from first paint (server render included): the loader shows
          until the model draws, then the model fades in like video does. */}
      <div
        ref={modelViewerRef}
        aria-busy={!modelReady}
        className={`transition-opacity duration-150 ${modelReady ? 'opacity-100' : 'opacity-0'}`}
        style={{ position: 'absolute', inset: 0 }}
      />
      {/* Cross-fades with the model over the same 150 ms, so no frame shows
          neither; hidden (not unmounted) once the model is in. */}
      <div
        role={modelReady ? undefined : 'status'}
        aria-label={modelReady ? undefined : ui.loading}
        aria-hidden={modelReady || undefined}
        className={`pointer-events-none absolute inset-0 flex items-center justify-center transition-[opacity,visibility] duration-150 ${modelReady ? 'invisible opacity-0' : 'visible opacity-100'}`}
      >
        <Loader size="md" />
      </div>
    </div>
  );
}
